import { createThemeManager } from './core/theme/theme-manager.js';
import { createDesignManager } from './core/design/design-manager.js';
import { createBrandLoader } from './core/brand/brand-loader.js';
import { ALLOWED_BRAND_OVERRIDES, isValidBrandValue } from './core/brand/brand-overrides.js';
import { createAppShell } from './modules/app-shell/app-shell.js';
import { createLoginView } from './components/login/login-view.js';
import { createToastManager } from './components/toast/toast.js';

/**
 * Testable boot logic. Creates theme manager, gates on auth (login view vs. app
 * shell), and loads brand.
 *
 * @param {Record<string, any> & {
 *   authService: import('./contracts/auth.js').AuthService,
 *   staticBrandConfig?: import('./contracts/brand.js').BrandConfig,
 *   loginLogoUrl?: string,
 *   loginLogoAlt?: string,
 * }} deps
 * @returns {{ destroy(): void }}
 */
export function bootstrapApp(deps) {
  const { apiClient, authService, staticBrandConfig, availableDesigns, defaultDesign, localeConfig, loginLogoUrl, loginLogoAlt, ...otherDeps } = deps;
  const { eventBus } = otherDeps;

  // 1. Theme + design managers (sync — read localStorage, reconcile DOM)
  const themeManager = createThemeManager();
  const designManager = createDesignManager({
    availableDesigns: availableDesigns || ['default'],
    defaultDesign: defaultDesign || 'default',
  });

  // 2. Toast manager + subscription before any view (catches init-time events)
  const toastManager = createToastManager(document.body);
  /** @type {(() => void) | undefined} */
  let offToast;
  if (eventBus) {
    offToast = eventBus.on('toast.message.requested', (/** @type {unknown} */ data) => {
      toastManager.show(/** @type {import('./contracts/events.js').ToastPayload} */ (data));
    });
  }

  // Cached brand state. Starts from the synchronous static config and is merged
  // with the API-loaded brand once it resolves (below). `showShell()` reads this
  // at mount time, so a shell mounted AFTER brand resolves (the normal
  // unauthenticated boot → login → sign-in flow, and every logout→relogin) still
  // gets the loaded title/logo — not just static defaults. Login is
  // static-brand-only (reads this once at first paint, no async update).
  let brandConfig = { appTitle: 'Admin', ...staticBrandConfig };

  let destroyed = false;
  // Bumped on every view swap AND on destroy; a late login success only proceeds
  // to the shell if its captured version is still current (defensive second layer
  // atop the authoritative service epoch).
  let transitionVersion = 0;

  /** @type {{ destroy(): void } | null} */
  let activeView = null;
  /** @type {ReturnType<typeof createAppShell> | null} */
  let shell = null;

  function destroyActiveView() {
    if (shell) shell = null;
    if (activeView) {
      activeView.destroy();
      activeView = null;
    }
  }

  function showShell() {
    transitionVersion += 1;
    destroyActiveView();
    shell = createAppShell(document.body, /** @type {any} */ ({
      ...otherDeps,
      apiClient,
      auth: authService.getState(),
      onLogout,
      themeManager,
      designManager,
      brandConfig,
      localeConfig,
    }));
    activeView = shell;
  }

  function showLogin() {
    transitionVersion += 1;
    destroyActiveView();
    activeView = createLoginView(document.body, {
      onSubmit: handleLoginSubmit,
      eventBus,
      appTitle: brandConfig.appTitle,
      // Prefer an explicit login logo; fall back to the brand logo if provided.
      logoUrl: loginLogoUrl ?? brandConfig.logoUrl,
      logoAlt: loginLogoAlt,
    });
  }

  /**
   * Delegates to the service, then owns the login→shell transition on success.
   * Returns the raw result to the view so it can render the failure states.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<import('./contracts/auth.js').LoginResult>}
   */
  async function handleLoginSubmit(email, password) {
    const versionAtStart = transitionVersion;
    const result = await authService.login(email, password);
    // Only advance to the shell if nothing tore this view down / swapped it
    // meanwhile (the service epoch already blocks a stale commit; this blocks a
    // stale DOM mount).
    if (result.ok && !destroyed && transitionVersion === versionAtStart) {
      showShell();
    }
    return result;
  }

  function onLogout() {
    authService.logout();
    showLogin();
  }

  // 3. Initial gate
  if (authService.getState().authenticated) {
    showShell();
  } else {
    showLogin();
  }

  // 4. Load brand config in background (async — may call API). Caches the result
  //    into `brandConfig` so a shell mounted later still receives it; a late
  //    resolution after destroy is a no-op.
  createBrandLoader({
    apiClient,
    staticConfig: staticBrandConfig,
  }).load().then((loadedBrand) => {
    if (destroyed) return;
    if (!document.body?.isConnected || !document.documentElement) return;

    // Cache for any shell mounted from here on (login→sign-in, logout→relogin).
    brandConfig = { ...brandConfig, ...loadedBrand };

    // Apply allowlisted + value-validated CSS overrides.
    if (loadedBrand.themeOverrides) {
      for (const [key, value] of Object.entries(loadedBrand.themeOverrides)) {
        if (ALLOWED_BRAND_OVERRIDES.has(key) && isValidBrandValue(key, value)) {
          document.documentElement.style.setProperty(key, value);
        }
      }
    }

    // If the shell is already the active view, update its brand display now.
    if (shell) shell.applyBrand(loadedBrand);
  }).catch(() => {}); // silent — defaults already applied

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      transitionVersion += 1;
      // Cancel + invalidate any in-flight login so it commits nothing.
      authService.invalidatePending();
      if (offToast) offToast();
      destroyActiveView();
      toastManager.destroy();
      themeManager.destroy();
      designManager.destroy();
    },
  };
}
